import { Box, Grid, debounce, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import EmptyActivityLibrary from '../EmptyActivityLibrary';
import ActivityLibraryComponent from '../ActivityLibrary';
import { useAppSelector } from '../../../redux/hooks';
import {
  IFolderType,
  getUserFoldersAndActivities,
  setUserFolders,
} from '../../../redux/activityLibrary/activityLibrarySlice';
import ActivityLibraryTopHeader from '../ActivityLibraryTopHeader';
import {
  ActivityLibraryFoldersLoadingView,
  ActivityLibrarySearchBarLoadingView,
} from '../ActivityLibraryLoadingView';
import { ACTIVITY_LIBRARY_ROUTE } from '../../../utils/routes';

const ActivityLibrary = () => {
  const [searchText, setSearchText] = useState('');
  const [exisitingCoach, setExistingCoach] = useState<boolean>(false);
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const dispatch = useDispatch();
  const { folders, activityLibraries } = useAppSelector(
    (state) => state.activityLibrary.userFoldersAndActivities
  );
  const { getUserFoldersAndActivitiesLoading, userFoldersAndActivities, userFolders } =
    useAppSelector((state) => state.activityLibrary);
  const { isDeleteActivitySuccess } = useAppSelector((state) => state.activityLibrary);
  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    if (!coachId) return;
    dispatch(getUserFoldersAndActivities({ coachId }));
  }, [coachId]);

  const debouncedFetchUserFoldersAndActivities = useCallback(
    debounce(({ filterValue }: { filterValue: string }) => {
      if (!coachId) return;
      dispatch(
        getUserFoldersAndActivities({
          coachId,
          filterValue,
        })
      );
    }, 1000),
    [coachId, searchText, dispatch]
  );

  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    debouncedFetchUserFoldersAndActivities({ filterValue: event.target.value });
  };

  useEffect(() => {
    if ((folders.length > 0 || activityLibraries.length > 0) && !exisitingCoach) {
      setExistingCoach(true);
    }
  }, [folders, activityLibraries]);

  useEffect(() => {
    if (isDeleteActivitySuccess && coachId) {
      dispatch(getUserFoldersAndActivities({ coachId, filterValue: searchText }));
    }
  }, [isDeleteActivitySuccess]);

  useEffect(() => {
    // update user folders with newly added folders
    const newFolders = userFoldersAndActivities.folders.filter(
      (folder) =>
        folder.folderType === IFolderType.NORMAL && !userFolders.find((v) => v.id === folder.id)
    );

    dispatch(setUserFolders(userFolders.concat(newFolders)));
  }, [userFoldersAndActivities]);

  if (location.pathname === ACTIVITY_LIBRARY_ROUTE) {
    return (
      <Box height="100%">
        {getUserFoldersAndActivitiesLoading && (
          <Grid gap={4} display="flex" flexDirection="column">
            <ActivityLibrarySearchBarLoadingView />
            <ActivityLibraryFoldersLoadingView />
          </Grid>
        )}

        {!getUserFoldersAndActivitiesLoading &&
          (folders.length === 0 && activityLibraries.length === 0 && !exisitingCoach ? (
            <EmptyActivityLibrary />
          ) : (
            <Grid gap={4} display="flex" flexDirection="column" height="100%">
              <ActivityLibraryTopHeader searchHandler={searchHandler} searchText={searchText} />
              <ActivityLibraryComponent folders={folders} activityLibraries={activityLibraries} />
            </Grid>
          ))}
      </Box>
    );
  }
  return (
    <Box height="100%" borderLeft={theme.borderStyles.commonBorders} width="100%">
      {getUserFoldersAndActivitiesLoading && (
        <Grid gap={4} display="flex" flexDirection="column" p={4}>
          <ActivityLibrarySearchBarLoadingView />
          <ActivityLibraryFoldersLoadingView />
        </Grid>
      )}

      {!getUserFoldersAndActivitiesLoading &&
        (folders.length === 0 && activityLibraries.length === 0 && !exisitingCoach ? (
          <EmptyActivityLibrary />
        ) : (
          <Grid
            gap={4}
            display="flex"
            flexDirection="column"
            bgcolor="rgba(255, 221, 200, 0.25)"
            p={4}
            minHeight="100%"
          >
            <ActivityLibraryTopHeader searchHandler={searchHandler} searchText={searchText} />
            <ActivityLibraryComponent folders={folders} activityLibraries={activityLibraries} />
          </Grid>
        ))}
    </Box>
  );
};

export default ActivityLibrary;
